import { useCallback, useState } from "react";
import { Pressable, Text, View } from "react-native";
import MaterialCommunityIcons from "react-native-vector-icons/MaterialCommunityIcons";
import useEspaceValideurStyle, { couleursValideur } from "./EspaceValideur.styles";
import { CarteDemandeProps } from "./EspaceValideur.types";
import { useSessionStore } from "../../lib/Store/sessionStore.component";
import { estAbsence, estTerminee, texteDuree, textePeriode } from "./EspaceValideur.utils";

export default function CarteDemande({demande, etat, commentaire, onOpen, onValider, selectionnee, onToggleSelection}: CarteDemandeProps){
    const { styles } = useEspaceValideurStyle();
    const { getCurrentInfos } = useSessionStore();
    const heuresParJour = getCurrentInfos().currentEntite?.collectivitegr?.dureeJourEnHeureCet || 7;
    const absence = estAbsence(demande);
    const couleur = demande.natureAbsence?.couleurHexa || couleursValideur.grey450;
    const libelleNature = demande.natureAbsence?.libelle || '';
    // Tant que la chaîne de validation n'est pas terminée, le valideur peut revenir sur sa réponse
    const modifiable = !estTerminee(demande);
    const validee = etat === 'valide';
    const refusee = etat === 'refuse';

    const handleOpen = useCallback(() => onOpen(demande), [demande, onOpen])
    const [envoiEnCours, setEnvoiEnCours] = useState(false);

    // La validation est envoyée directement, sans passer par la saisie d'un commentaire
    const handleValider = useCallback(() => {
        if(envoiEnCours){
            return;
        }
        setEnvoiEnCours(true);
        onValider(demande).finally(() => setEnvoiEnCours(false));
    }, [demande, envoiEnCours, onValider])
    const handleRefuser = useCallback(() => onOpen(demande, 'refuse'), [demande, onOpen])
    const handleToggleSelection = useCallback(() => onToggleSelection?.(demande), [demande, onToggleSelection])

    return (
        <Pressable onPress={handleOpen} style={[styles.carte, selectionnee && { borderColor: couleursValideur.primaire }]}>
            <View style={[styles.carteBarre, { backgroundColor: couleur }]}/>
            <View style={styles.carteContenu}>
                <View style={styles.carteLigne}>
                    {etat === 'attente' && onToggleSelection && (
                        <Pressable
                            onPress={handleToggleSelection}
                            hitSlop={8}
                            accessibilityRole="checkbox"
                            accessibilityState={{ checked: !!selectionnee }}
                            style={[styles.caseACocher, selectionnee && styles.caseACocherActive]}
                        >
                            {selectionnee && <MaterialCommunityIcons name="check" size={13} color={couleursValideur.blanc}/>}
                        </Pressable>
                    )}
                    <View style={styles.carteIdentite}>
                        <Text style={styles.carteAgent} numberOfLines={1}>{demande.agent?.nomClassement}</Text>
                        <Text style={[styles.carteNature, { color: couleur }]}>
                            {absence ? libelleNature : `Épargne · ${libelleNature}`}
                        </Text>
                    </View>
                    <View style={[styles.carteType, { backgroundColor: absence ? couleursValideur.bleuClair : couleursValideur.metierTonique }]}>
                        <Text style={[styles.carteTypeTexte, { color: absence ? couleursValideur.bleu : couleursValideur.metierSecondaire }]}>
                            {absence ? 'Absence' : 'CET'}
                        </Text>
                    </View>
                </View>
                <View style={styles.carteDetail}>
                    <Text style={styles.cartePeriode} numberOfLines={1}>{textePeriode(demande)}</Text>
                    <Text style={styles.carteDuree}>{texteDuree(demande, heuresParJour)}</Text>
                </View>
                {etat !== 'attente' && (
                    <View style={styles.carteCommentaire}>
                        <MaterialCommunityIcons name="message-text-outline" size={13} color={couleursValideur.grey600}/>
                        <Text style={styles.carteCommentaireTexte} numberOfLines={1}>{commentaire || 'Sans commentaire'}</Text>
                    </View>
                )}
                {modifiable && (
                    <View style={styles.carteBoutons}>
                        <Pressable
                            onPress={handleValider}
                            disabled={envoiEnCours || validee}
                            style={[
                                styles.carteBouton,
                                { borderColor: couleursValideur.vertFg, backgroundColor: refusee ? couleursValideur.blanc : couleursValideur.vertBg },
                                envoiEnCours && { opacity: 0.6 },
                            ]}
                        >
                            <MaterialCommunityIcons name="check" size={14} color={couleursValideur.vertFg}/>
                            <Text style={[styles.carteBoutonTexte, { color: couleursValideur.vertFg }]}>{validee ? 'Validée' : 'Valider'}</Text>
                        </Pressable>
                        <Pressable
                            onPress={handleRefuser}
                            disabled={refusee}
                            style={[styles.carteBouton, { borderColor: couleursValideur.danger, backgroundColor: refusee ? couleursValideur.roseBg : couleursValideur.blanc }]}
                        >
                            <MaterialCommunityIcons name="close" size={14} color={couleursValideur.danger}/>
                            <Text style={[styles.carteBoutonTexte, { color: couleursValideur.danger }]}>{refusee ? 'Refusée' : 'Refuser'}</Text>
                        </Pressable>
                    </View>
                )}
            </View>
        </Pressable>
    )
}
